import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ObjectDataEditor } from './ObjectDataEditor';
import { contentApi } from '@/lib/api';
import { itemsApi } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
type ObjectType = 'content' | 'item';
interface ObjectDataEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  objectType: ObjectType;
  objectId: number;
}
async function fetchObjectData(objectType: ObjectType, objectId: number): Promise<Record<string, unknown>> {
  switch (objectType) {
    case 'content':
      {
        const content = await contentApi.getContentItem(objectId);
        return content.data as Record<string, unknown> ?? {};
      }
    case 'item':
      {
        const item = await itemsApi.getItem(String(objectId));
        return item.data as Record<string, unknown> ?? {};
      }
    default:
      throw new Error(`Unsupported object type: ${objectType}`);
  }
}
async function updateObjectData(objectType: ObjectType, objectId: number, data: Record<string, unknown>): Promise<void> {
  switch (objectType) {
    case 'content':
      await contentApi.updateContent(objectId, {
        data
      });
      break;
    case 'item':
      await itemsApi.updateItem(objectId, {
        data
      } as any);
      break;
    default:
      throw new Error(`Unsupported object type: ${objectType}`);
  }
}
export function ObjectDataEditorDialog({
  isOpen,
  onClose,
  objectType,
  objectId
}: ObjectDataEditorDialogProps) {
  const queryClient = useQueryClient();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [editedData, setEditedData] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch data when dialog opens
  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setEditedData(null);
      return;
    }
    setIsLoading(true);
    fetchObjectData(objectType, objectId).then(fetchedData => {
      setData(fetchedData);
      setEditedData(fetchedData);
    }).catch(error => {
      console.error('Failed to fetch object data:', error);
      toast.error('Failed to load data');
      onClose();
    }).finally(() => {
      setIsLoading(false);
    });
  }, [isOpen, objectType, objectId, onClose]);

  // Ensure body scroll is restored when dialog closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  const handleSave = async () => {
    if (!editedData) return;
    setIsSaving(true);
    try {
      await updateObjectData(objectType, objectId, editedData);
      toast.success('Data updated successfully');

      // Invalidate relevant queries
      if (objectType === 'content') {
        queryClient.invalidateQueries({
          queryKey: ['content']
        });
      } else if (objectType === 'item') {
        queryClient.invalidateQueries({
          queryKey: ['items']
        });
      }
      onClose();
    } catch (error_0) {
      console.error('Failed to update object data:', error_0);
      toast.error('Failed to save data');
    } finally {
      setIsSaving(false);
    }
  };
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:!max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Data</DialogTitle>
          <DialogDescription>
            Edit the data column for {objectType} #{objectId}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {isLoading ? <div className="flex items-center justify-center h-[400px]">
              <div className="text-gray-500">Loading...</div>
            </div> : <ObjectDataEditor data={editedData} onChange={setEditedData} />}
        </div>

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving} className="cursor-pointer">
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving || isLoading} className="cursor-pointer">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}